import { useQuery } from '@apollo/client';
import { getValidationSchema } from 'components/exams/Dropzone/dropzone.yup';
import { GET_USER_UNREAD_NOTIFICATIONS_COUNT } from 'components/notifications/notifications.api';
import { useRemoveExam } from 'components/pages/CreateExam/ExamPanel/examPanel.hooks';
import {
  canceledToastProps,
  errorToastProps,
  successfulToastProps,
  uploadingToastProps,
} from 'components/pages/CreateExam/ExamPanel/UploadTab/hooks/constants';
import { OutlinedButton, SubmitButton } from 'components/system/buttons';
import ContainerLoading from 'components/system/ContainerLoading/ContainerLoading';
import {
  dismissToast,
  displayToast,
  updateToast,
} from 'components/system/Toast';
import { TECHNOLOGY_UPLOAD_MAX_FILES } from 'constants/products';
import {
  EnumNotificationType,
  useRemoveExamNotificationsMutation,
} from 'generated/graphql';
import { Types } from 'mongoose';
import { useFeedbackModal } from 'providers';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ExamObject } from 'types/mongoose-types';
import { getAxiosTooManyRequestsFeedbackModalProps } from 'utils/getAxiosTooManyRequestsFeedbackModalProps';
import { logError } from 'utils/logError';
import { DEVICES } from 'utils/products/devices';
import useYupValidationResolver from 'utils/useYupValidationResolver';
import Dropzone from '../Dropzone/Dropzone';
import { useUpdateExam } from '../examForms/examForms.hooks';
import { GET_EXAM_FOR_REUPLOAD } from './reuploadExamModalForm.api';
import { handleUploadSubmit } from './reuploadExamModalForm.helpers';
import { StyledFooter } from './reuploadExamModalForm.styles';
import { IReuploadExamModalForm } from './reuploadExamModalForm.types';

const ReuploadExamModalForm = ({
  examId,
  onClose,
  onCompleted,
}: IReuploadExamModalForm) => {
  const [isUploading, setIsUploading] = useState(false);
  const { data, loading } = useQuery<{ exam: ExamObject }>(
    GET_EXAM_FOR_REUPLOAD,
    { variables: { examId }, skip: !examId },
  );
  const [removeNotifications] = useRemoveExamNotificationsMutation({
    refetchQueries: [GET_USER_UNREAD_NOTIFICATIONS_COUNT],
  });

  const technology = data?.exam?.technology;
  const deviceInfo = DEVICES.find(({ id }) => id === data?.exam?.device);

  const {
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    watch,
    clearErrors,
  } = useForm<{ files: File[] }>({
    mode: 'onBlur',
    resolver: useYupValidationResolver(
      getValidationSchema(
        technology ? TECHNOLOGY_UPLOAD_MAX_FILES[technology] : 0,
        deviceInfo?.acceptedExtensions ?? '',
      ),
    ),
  });

  const { updateExam } = useUpdateExam(new Types.ObjectId(examId));
  const { removeExam } = useRemoveExam();
  const { openFeedbackModal } = useFeedbackModal();

  if (loading || !deviceInfo || !technology) {
    return <ContainerLoading />;
  }

  const onSubmit = async (values: { files: File[] }) => {
    const toastId = displayToast(uploadingToastProps, {
      autoClose: false,
    });

    try {
      if (!data?.exam) {
        return;
      }

      setIsUploading(true);
      onClose();

      await handleUploadSubmit(values, data.exam, updateExam).then(() => {
        onCompleted?.();
        setIsUploading(false);

        updateToast(toastId, successfulToastProps, { autoClose: false });

        void removeNotifications({
          variables: {
            examId: data.exam._id.toString(),
            notificationType: EnumNotificationType.ExamUploadError,
          },
        });
      });
    } catch (error) {
      setIsUploading(false);

      if (error instanceof Error && error.message === 'Upload canceled') {
        updateToast(toastId, canceledToastProps, { autoClose: false });
        return;
      }

      logError({ error });
      const modalProps = getAxiosTooManyRequestsFeedbackModalProps(error);

      if (modalProps) {
        openFeedbackModal(modalProps);
        dismissToast(toastId);
        return;
      }

      updateToast(toastId, errorToastProps, { autoClose: false });
    }
  };

  const onDelete = () => {
    openFeedbackModal({
      type: 'warning',
      title: 'Delete exam',
      message: 'Are you sure you want to delete this exam?',
      cancelBtn: {
        label: 'No',
      },
      submitBtn: {
        label: 'Delete',
        onClick: async () => {
          await removeExam({ variables: { removeExamId: data?.exam._id } })
            .then(() => {
              onClose();
            })
            .catch((error: unknown) => logError({ error }));
        },
      },
    });
  };

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Dropzone
        files={watch('files')}
        setValue={setValue}
        maxFiles={TECHNOLOGY_UPLOAD_MAX_FILES[technology]}
        acceptedExtensions={deviceInfo.acceptedExtensions}
        clearYupErrors={() => {
          clearErrors(['files']);
        }}
        yupError={errors.files}
        isUploading={isUploading}
      />
      <StyledFooter handleCancel={onClose}>
        <OutlinedButton
          variant="contained"
          onClick={onDelete}
          disabled={isSubmitting}
        >
          DELETE EXAM
        </OutlinedButton>
        <SubmitButton variant="contained" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'UPLOAD'}
        </SubmitButton>
      </StyledFooter>
    </form>
  );
};

export default ReuploadExamModalForm;
